import type { ReactNode } from "react";

interface ProgressRingProps {
  value: number;
  target: number;
  label: string;
  children: ReactNode;
  displayValue?: ReactNode;
  sublabel?: ReactNode;
}

export default function ProgressRing({
  value,
  target,
  label,
  children,
  displayValue,
  sublabel,
}: ProgressRingProps) {
  const pct = target > 0 ? Math.min(value / target, 1) : 0;
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const dash = circumference * pct;

  return (
    <div className="flex min-w-0 flex-col items-center text-center">
      <div className="relative h-28 w-28">
        <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            className="stroke-zinc-200 dark:stroke-zinc-800"
            strokeWidth="8"
          />
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            className="stroke-accent"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${circumference - dash}`}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-50 text-accent-700 dark:bg-accent-950/50 dark:text-accent-300">
            {children}
          </div>
        </div>
      </div>
      <div className="mt-1 min-w-0">
        <p className="tnum text-2xl font-semibold leading-none text-zinc-950 dark:text-zinc-50">
          {displayValue ?? formatValue(value)}
        </p>
        <p className="mt-1 text-sm font-medium text-zinc-600 dark:text-zinc-300">
          {label}
        </p>
        {sublabel && (
          <p className="mt-1 text-xs leading-tight text-zinc-500">{sublabel}</p>
        )}
      </div>
    </div>
  );
}

function formatValue(value: number): string {
  if (!Number.isFinite(value)) return "—";
  return Math.round(value).toLocaleString("en-US");
}
